import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Camera, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useContent } from '../../context/ContentContext';
import api from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const GalleryPage = () => {
  const { c } = useContent();
  const [photos, setPhotos] = useState(null);

  useEffect(() => {
    api.get('/gallery').then((res) => setPhotos(res.data || [])).catch(() => setPhotos([]));
  }, []);

  return (
    <main className="pt-52" data-testid="gallery-page">
      {/* Hero */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              {c('gallery.hero.eyebrow', 'Our Story in Pictures')}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6">
              {c('gallery.hero.title', 'Photo Gallery')}
            </h1>
            <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
              {c('gallery.hero.text', 'As our first meetings, service days, and celebrations happen, the memories will live here — real photos of real women building something new in Lincoln County.')}
            </p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-white min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {photos === null && (
            <p className="text-center text-[#3c4a2a]/60 py-16">Loading…</p>
          )}
          {photos && photos.length === 0 && (
            <div className="text-center py-16 max-w-xl mx-auto" data-testid="gallery-empty-state">
              <div className="w-20 h-20 rounded-full bg-[#f1e3eb] flex items-center justify-center mx-auto mb-6">
                <Camera className="w-10 h-10 text-[#bd8cae]" />
              </div>
              <h2 className="font-serif text-2xl text-[#3c4a2a] mb-3">
                Our Story Starts This Fall
              </h2>
              <p className="text-[#3c4a2a]/70 mb-8">
                Photos from our first membership meetings and events will appear here. 
                Want to be in them? Come to a meeting — see our Events page for upcoming dates.
              </p>
              <Link to="/events">
                <Button className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-8" data-testid="gallery-empty-events-btn">
                  See Upcoming Meetings
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
          {photos && photos.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
              {photos.map((photo) => (
                <figure
                  key={photo.id}
                  data-testid={`gallery-photo-${photo.id}`}
                  className="group rounded-xl overflow-hidden bg-[#f1e3eb] shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={`${API}/api/gallery/file/${photo.id}`}
                      alt={photo.caption || 'Women\'s Club of Lincoln County'}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {(photo.caption || photo.taken_at) && (
                    <figcaption className="p-4">
                      {photo.caption && <p className="text-[#3c4a2a] font-medium text-sm">{photo.caption}</p>}
                      {photo.taken_at && <p className="text-[#3c4a2a]/50 text-xs mt-1">{photo.taken_at}</p>}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default GalleryPage;
